import React from 'react';
import GeneralEditWizzardPage from './GeneralEditWizzardPageFigma';
import CrudTypesEnum from '../../Enums/CrudTypesEnum';
import ErrorMessage from '../ErrorMessage';

class GeneralEditWizzardBase extends React.Component {
  static defaultProps = {
    clickOnSave: () => {},
    clickOnCancel: () => {},
    crudType: CrudTypesEnum.Create,
    editedItem: null,
    errorCode: 'GEWBF12',
    onChangeEditedItem: () => {},
    approveBeforeSaveCheck: null,
    approveBeforeSaveMessage: null,
    windowDescription: {
      navigateToOnCreation: null, // A base url to navigate to after creation. The id of the created item will be added to the end of the url.
      pagesInGeneralEditWizzard: [],
      serviceFunction: (editedItem) => {}, //The function that will be called when the user clicks on 'Finish', it is in most cases a backend call.
      titleForCreate: 'Add New Item',
      titleForEdit: 'Edit Item',
      messageOnSuccess: 'Item has been saved.', // Can be a string or a function that returns a string. The function will be called with the edited item as a parameter.
      suppressSuccessMessage: false, // Silent save: skip the success popup after a successful save (clickOnSave still runs).
      showErrorList: false,
    },
  };

  constructor(props) {
    super(props);

    let tempEditedItem;
    if (props.editedItem) {
      tempEditedItem = { ...props.editedItem };
    } else {
      let newItemType;
      try {
        newItemType = new props.windowDescription.itemType();
      } catch (e) {
        newItemType = new props.windowDescription.itemType()();
      }
      tempEditedItem = newItemType;
    }

    this.state = {
      step: 0,
      editedItem: tempEditedItem,
      visitedSteps: [0],
      // A save rejected by the backend with a user-facing message (ProblemDetails business error or a
      // legacy `{ error }` envelope). Shown inside the dialog's footer by GeneralEditWizzardPage — next to
      // the button that triggered it — instead of as a top-right toast, which is easy to miss behind the
      // confirm popup and the loading overlay. Cleared on the next field change or save attempt.
      serverError: null,
    };
    this.clearServerError = this.clearServerError.bind(this);

    this.clickOnSaveInWizzardPage = this.clickOnSaveInWizzardPage.bind(this);
    this.serviceBaseFunction = this.serviceBaseFunction.bind(this);
    this.clickOnDeleteInWizzardPage = this.clickOnDeleteInWizzardPage.bind(this);
  }

  clickOnDeleteInWizzardPage(edited) {
    ErrorMessage.SetApprove(
      this.props.windowDescription.deleteConfirmMessage ?? 'Are you sure you want to delete this?',
      () => this.deleteBaseFunction(edited)
    );
  }

  deleteBaseFunction(edited) {
    ErrorMessage.SetLoading(true);
    this.props.windowDescription
      .deleteFunction({ ...edited })
      .then((response) => {
        ErrorMessage.SetLoading(false);
        if (response && (response.error || response.errorMessage)) {
          ErrorMessage.SetErrorMessage(response.error || response.errorMessage);
          return;
        }
        ErrorMessage.SetSuccess(
          this.props.windowDescription.messageOnDelete ?? 'Item has been deleted.',
          this.props.clickOnSave
        );
      })
      .catch(() => {
        ErrorMessage.SetLoading(false);
        ErrorMessage.SetErrorMessage(`Error! ErrorCode: GEWBF-DEL - ${this.props.errorCode}`);
      });
  }

  componentDidMount() {}

  clickOnSaveInWizzardPage(newStep, edited) {
    if (newStep === this.props.windowDescription.pagesInGeneralEditWizzard.length) {
      if (this.props.approveBeforeSaveCheck && this.props.approveBeforeSaveCheck(edited)) {
        const rawMessage = this.props.approveBeforeSaveMessage;
        const message =
          (typeof rawMessage === 'function' ? rawMessage(edited) : rawMessage) ?? 'Are you sure you want to save?';
        ErrorMessage.SetApprove(message, () => this.serviceBaseFunction(edited));
        return;
      }
      this.serviceBaseFunction(edited);
      return;
    }
    this.setState((prev) => ({
      step: newStep,
      editedItem: edited,
      visitedSteps: prev.visitedSteps.includes(newStep) ? prev.visitedSteps : [...prev.visitedSteps, newStep],
    }));
  }

  clearServerError() {
    if (this.state.serverError !== null) {
      this.setState({ serverError: null });
    }
  }

  // A rejected save: business messages stay in the dialog (serverError → footer), infrastructure failures
  // keep the generic error-code toast so the user has something to report.
  showSaveError(error, edited, fallbackCode) {
    if (error?.isBusinessError) {
      this.setState({ step: 0, editedItem: edited, serverError: error.message });
      return;
    }
    this.setState({ step: 0, editedItem: edited });
    ErrorMessage.SetErrorMessage(`Error! ErrorCode: ${fallbackCode} - ${this.props.errorCode}`);
  }

  // Every clickOnSave callback below must get `edited`, NOT this.state.editedItem: clickOnSaveInWizzardPage skips
  // its setState on the final step (it saves instead of advancing), so state still holds the item from the
  // previous step transition - on a single-page wizard, the untouched initial item.
  serviceBaseFunction(edited) {
    this.clearServerError();
    if (
      this.props.windowDescription.waitForServiceFunction &&
      !this.props.windowDescription.waitForServiceFunction(edited)
    ) {
      this.props.windowDescription.serviceFunction({ ...edited }).catch((error) => {
        this.showSaveError(error, edited, 'GEWBF79');
      });

      if (!this.props.windowDescription.suppressSuccessMessage) {
        const messageOnSuccess =
          typeof this.props.windowDescription.messageOnSuccess === 'function'
            ? this.props.windowDescription.messageOnSuccess(edited)
            : this.props.windowDescription.messageOnSuccess;
        ErrorMessage.SetInfo(
          messageOnSuccess ??
            'Item will be saved in the background. You will receive an in-app notification when the process is done.'
        );
      }
      if (this.props.clickOnSave) this.props.clickOnSave(edited);
      return;
    }

    ErrorMessage.SetLoading(true);
    this.props.windowDescription
      .serviceFunction({ ...edited })
      .then((response) => {
        ErrorMessage.SetLoading(false);
        if (this.props.windowDescription.showErrorList) {
          if (response.errorList) {
            const errorJsx = (
              <div>
                {response.errorList.map((error) => {
                  return (
                    <>
                      {error}
                      <br />
                    </>
                  );
                })}
              </div>
            );
            this.props.clickOnCancel();

            ErrorMessage.SetWarning(errorJsx, 'Error');
            return;
          } else if (response.error || response.errorMessage) {
            this.props.clickOnCancel();
            const error = response.error ? response.error : response.errorMessage;
            ErrorMessage.SetWarning(error, 'Error');
            if (this.props.windowDescription.errorHandler) {
              this.props.windowDescription.errorHandler(response, edited);
              this.setState({
                step: 0,
                editedItem: edited,
              });
            }
            return;
          }
        }

        if (response.error || response.errorMessage) {
          const error = response.error ? response.error : response.errorMessage;
          this.setState({
            step: 0,
            editedItem: edited,
            serverError: error,
          });
          if (this.props.windowDescription.errorHandler) {
            this.props.windowDescription.errorHandler(error, edited);
            this.setState({
              step: 0,
              editedItem: edited,
            });
          }
          return;
        }

        if (response.warning) {
          ErrorMessage.SetWarning(response.warning);
          if (this.props.clickOnSave) this.props.clickOnSave(edited, response);
          return;
        }

        if (response.eventResult) {
          const message = (
            <div>
              Result of process:
              <ul>
                {response.eventResult.success.map((s) => (
                  <li>{s}</li>
                ))}
                {response.eventResult.failure.map((s) => (
                  <li>{s}</li>
                ))}
              </ul>
            </div>
          );
          if (this.props.clickOnSave) this.props.clickOnSave(edited, response);
          ErrorMessage.SetInfo(message);
          return;
        }

        if (response && response.navigationId && this.props.windowDescription.navigateToOnCreation) {
          if (this.props.clickOnSave) this.props.clickOnSave(edited, response);
          const tab = window.open(this.props.windowDescription.navigateToOnCreation + response.navigationId, '_blank');
          if (tab) tab.focus();
          return;
        }

        // Silent save: no confirmation popup, so run the clickOnSave follow-up right away — it is normally
        // triggered by the popup's OK button, and skipping it would leave the panel open and the list stale.
        if (this.props.windowDescription.suppressSuccessMessage) {
          if (this.props.clickOnSave) this.props.clickOnSave(edited, response);
          return;
        }

        ErrorMessage.SetSuccess(
          this.props.windowDescription.messageOnSuccess
            ? typeof this.props.windowDescription.messageOnSuccess === 'function' &&
              this.props.windowDescription.messageOnSuccess(edited)
              ? this.props.windowDescription.messageOnSuccess(edited)
              : this.props.windowDescription.messageOnSuccess
            : 'Item has been saved.',
          () => this.props.clickOnSave && this.props.clickOnSave(edited, response)
        );
      })
      .catch((error) => {
        ErrorMessage.SetLoading(false);
        this.showSaveError(error, edited, 'GEWBF191');
      });
  }

  render() {
    return (
      <div>
        <GeneralEditWizzardPage
          errorCode={this.props.errorCode}
          step={this.state.step}
          crudType={this.props.crudType}
          windowDescription={this.props.windowDescription}
          clickOnCancel={this.props.clickOnCancel}
          clickOnSave={this.clickOnSaveInWizzardPage}
          clickOnDelete={this.clickOnDeleteInWizzardPage}
          editedItem={this.state.editedItem}
          visitedSteps={this.state.visitedSteps}
          onChangeEditedItem={this.handleOnChangeEditedItem()} //{this.props.onChangeEditedItem(')}})})}
          serverError={this.state.serverError}
          onClearServerError={this.clearServerError}
        />
      </div>
    );
  }

  handleOnChangeEditedItem() {
    return this.props.onChangeEditedItem;
  }
}

export default GeneralEditWizzardBase;
